import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { catchError, EMPTY, finalize, take } from 'rxjs';
import { Invoice, InvoiceAddress, InvoiceCustomer } from '../../../domain/entities/invoice.entity';
import { InvoiceStatus } from '../../../domain/enums/invoice-status.enum';
import { InvoiceRevenueEditData, InvoiceRevenueEditRequest } from '../../../domain/models/invoice-revenue-edit.model';
import { InvoiceFacade } from '../../facades/invoice.facade';
import { createInvoiceRevenueAddressForm, InvoiceRevenueAddressForm } from '../../forms/invoice-revenue-address.form';

type EditForm = FormGroup<{
  customer: FormGroup<{
    name: FormControl<string>;
    taxId: FormControl<string>;
  }>;
  address: InvoiceRevenueAddressForm;
}>;

@Component({
  selector: 'app-invoice-revenue-edit-modal',
  templateUrl: './invoice-revenue-edit-modal.component.html',
  styleUrls: ['./invoice-revenue-edit-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvoiceRevenueEditModalComponent implements OnChanges {
  @Input() invoice: Invoice | null = null;

  @Output() readonly success = new EventEmitter<void>();
  @Output() readonly cancel = new EventEmitter<void>();

  loading = false;
  saving = false;
  errorMessage: string | null = null;
  private originalData: InvoiceRevenueEditData | null = null;

  readonly form: EditForm = new FormGroup({
    customer: new FormGroup({
      name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      taxId: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    }),
    address: createInvoiceRevenueAddressForm(),
  });

  constructor(
    private readonly facade: InvoiceFacade,
    private readonly messageService: MessageService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['invoice']) return;

    if (!this.invoice) {
      this.form.reset();
      this.errorMessage = null;
      this.loading = false;
      this.saving = false;
      this.originalData = null;
      return;
    }

    this.patchForm(this.invoice.customer, this.invoice.address);
    this.originalData = {
      invoiceId: this.invoice.id,
      revenueId: this.invoice.revenueId,
      customer: this.invoice.customer,
      address: this.invoice.address,
    };
    this.setEditability();
    this.loadEditData(this.invoice);
  }

  get visible(): boolean {
    return this.invoice !== null;
  }

  get canEditPayerAndAddress(): boolean {
    const status = this.invoice?.status;
    return status === InvoiceStatus.Failed ||
      status === InvoiceStatus.Pending ||
      status === InvoiceStatus.Canceled;
  }

  get submitDisabled(): boolean {
    return !this.canEditPayerAndAddress || this.loading || this.saving;
  }

  onCancel(): void {
    if (this.saving) return;
    this.cancel.emit();
  }

  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.onCancel();
    }
  }

  onSubmit(): void {
    if (!this.invoice || this.saving || this.loading) return;

    if (!this.canEditPayerAndAddress) {
      this.errorMessage = 'Edição de pagador e endereço permitida apenas para notas com status Falha, Pendente ou Cancelado.';
      this.cdr.markForCheck();
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const request = this.toRequest(this.invoice);
    if (!request.payer && !request.address) {
      this.messageService.add({
        severity: 'info',
        summary: 'Sem alterações',
        detail: 'Nenhum dado foi alterado para salvar.'
      });
      return;
    }

    this.saving = true;
    this.errorMessage = null;

    this.facade.saveRevenueEdit(request).pipe(
      take(1),
      finalize(() => {
        this.saving = false;
        this.cdr.markForCheck();
      }),
      catchError(() => {
        this.errorMessage = 'Não foi possível salvar a edição da receita.';
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: this.errorMessage
        });
        this.cdr.markForCheck();
        return EMPTY;
      })
    ).subscribe(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Receita atualizada',
        detail: 'Dados da nota fiscal atualizados com sucesso.'
      });
      this.success.emit();
    });
  }

  private loadEditData(invoice: Invoice): void {
    this.loading = true;
    this.errorMessage = null;

    this.facade.getEditData(invoice).pipe(
      take(1),
      finalize(() => {
        this.loading = false;
        this.cdr.markForCheck();
      }),
      catchError(() => {
        this.errorMessage = 'Não foi possível carregar os dados atuais da receita.';
        this.cdr.markForCheck();
        return EMPTY;
      })
    ).subscribe((data) => {
      this.originalData = data;
      this.patchForm(data.customer, data.address);
      this.setEditability();
    });
  }

  private patchForm(customer: InvoiceCustomer, address: InvoiceAddress): void {
    this.form.patchValue({
      customer: {
        name: customer.name,
        taxId: customer.taxId,
      },
      address: {
        postalCode: address.postalCode,
        streetAddress: address.street,
        number: address.number,
        district: address.neighborhood,
        address2: address.complement,
        city: address.city,
        state: address.state,
        email: address.email ?? '',
        country: 'Brasil',
      }
    }, { emitEvent: false });
  }

  private setEditability(): void {
    if (!this.canEditPayerAndAddress) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.form.controls.customer.enable({ emitEvent: false });
    this.form.controls.address.controls.postalCode.enable({ emitEvent: false });
    this.form.controls.address.controls.number.enable({ emitEvent: false });
    this.form.controls.address.controls.address2.enable({ emitEvent: false });
    this.form.controls.address.controls.email.enable({ emitEvent: false });
    this.form.controls.address.controls.streetAddress.disable({ emitEvent: false });
    this.form.controls.address.controls.district.disable({ emitEvent: false });
    this.form.controls.address.controls.state.disable({ emitEvent: false });
    this.form.controls.address.controls.city.disable({ emitEvent: false });
    this.form.controls.address.controls.ibge.disable({ emitEvent: false });
    this.form.controls.address.controls.country.disable({ emitEvent: false });
  }

  private toRequest(invoice: Invoice): InvoiceRevenueEditRequest {
    const value = this.form.getRawValue();
    const currentCustomer: InvoiceCustomer = {
      name: value.customer.name.trim(),
      taxId: value.customer.taxId,
    };
    const currentAddress: InvoiceAddress = {
      postalCode: value.address.postalCode,
      street: value.address.streetAddress,
      number: value.address.number,
      neighborhood: value.address.district,
      complement: value.address.address2,
      city: value.address.city,
      state: value.address.state,
      email: value.address.email,
    };
    const originalCustomer = this.originalData?.customer ?? invoice.customer;
    const originalAddress = this.originalData?.address ?? invoice.address;
    const payer = this.diffCustomer(originalCustomer, currentCustomer);
    const address = this.diffAddress(originalAddress, currentAddress);

    return {
      invoiceId: invoice.id,
      revenueId: invoice.revenueId,
      ...(Object.keys(payer).length > 0 ? { payer } : {}),
      ...(Object.keys(address).length > 0 ? { address } : {}),
    };
  }

  private diffCustomer(original: InvoiceCustomer, current: InvoiceCustomer): Partial<InvoiceCustomer> {
    const changes: Partial<InvoiceCustomer> = {};
    this.assignChanged(changes, 'name', original.name, current.name);
    this.assignChanged(changes, 'taxId', original.taxId, current.taxId);
    return changes;
  }

  private diffAddress(original: InvoiceAddress, current: InvoiceAddress): Partial<InvoiceAddress> {
    const changes: Partial<InvoiceAddress> = {};
    this.assignChanged(changes, 'postalCode', original.postalCode, current.postalCode);
    this.assignChanged(changes, 'street', original.street, current.street);
    this.assignChanged(changes, 'number', original.number, current.number);
    this.assignChanged(changes, 'neighborhood', original.neighborhood, current.neighborhood);
    this.assignChanged(changes, 'complement', original.complement, current.complement);
    this.assignChanged(changes, 'city', original.city, current.city);
    this.assignChanged(changes, 'state', original.state, current.state);
    this.assignChanged(changes, 'email', original.email ?? '', current.email ?? '');
    return changes;
  }

  private assignChanged<T extends Record<string, any>>(changes: Partial<T>, key: keyof T, original: string, current: string): void {
    const normalizedOriginal = this.normalizeValue(original);
    const normalizedCurrent = this.normalizeValue(current);

    if (normalizedOriginal !== normalizedCurrent) {
      changes[key] = normalizedCurrent as T[keyof T];
    }
  }

  private normalizeValue(value: string | null | undefined): string {
    return String(value ?? '').trim();
  }
}
